import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { X } from 'lucide-react';

export function AgreementModal({ open, onClose, agreement, acceptanceRecord }) {
  if (!agreement) return null;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-3xl max-h-[90vh] p-0 rounded-2xl overflow-hidden" data-testid={`agreement-modal-${agreement.key}`}>
        <DialogHeader className="px-6 pt-6 pb-4 border-b border-gray-100 sticky top-0 bg-white z-10">
          <div className="flex items-center justify-between">
            <div>
              <DialogTitle className="font-['Outfit'] text-lg text-[#1F2937]">{agreement.title}</DialogTitle>
              <DialogDescription className="sr-only">{agreement.title} Version {agreement.version}</DialogDescription>
              <p className="text-xs text-gray-400 mt-0.5">Version {agreement.version}</p>
            </div>
            <Button variant="ghost" size="sm" onClick={onClose} className="h-8 w-8 p-0 rounded-full" data-testid="close-agreement-modal">
              <X className="h-4 w-4" />
            </Button>
          </div>
        </DialogHeader>
        <ScrollArea className="max-h-[calc(90vh-160px)]">
          <div className="px-6 py-5 space-y-4 text-sm text-[#374151] leading-relaxed">
            {agreement.sections.map((s, i) => {
              if (s.type === 'header') {
                return <h1 key={i} className="text-center text-lg font-bold text-[#1F2937] font-['Outfit'] pb-2">{s.text}</h1>;
              }
              if (s.type === 'meta') {
                return (
                  <div key={i} className="text-xs text-gray-500 space-y-0.5 pb-4 border-b border-gray-100">
                    {s.lines.map((l, j) => l ? <p key={j}>{l}</p> : <p key={j}>&nbsp;</p>)}
                  </div>
                );
              }
              if (s.type === 'heading') {
                return <h2 key={i} className="text-base font-bold text-[#1F2937] pt-4 pb-1 font-['Outfit']">{s.text}</h2>;
              }
              if (s.type === 'para') {
                return <p key={i} className="text-[#4B5563] italic">{s.text}</p>;
              }
              if (s.type === 'clause') {
                const lines = s.text.split('\n');
                return (
                  <div key={i} className="pl-1">
                    {lines.map((line, j) => {
                      if (j === 0) {
                        const dotIdx = line.indexOf(' ');
                        const num = line.substring(0, dotIdx);
                        const rest = line.substring(dotIdx);
                        return <p key={j}><span className="font-semibold text-[#1F2937]">{num}</span>{rest}</p>;
                      }
                      return <p key={j} className="pl-4 text-[#4B5563]">{line}</p>;
                    })}
                  </div>
                );
              }
              return null;
            })}

            {/* Electronic Acceptance Record */}
            <div className="mt-8 pt-6 border-t-2 border-gray-200">
              <h2 className="text-base font-bold text-[#1F2937] font-['Outfit'] mb-4">Electronic Acceptance Record</h2>
              <div className="bg-gray-50 rounded-xl p-5 space-y-3 text-sm">
                <div className="grid grid-cols-[180px_1fr] gap-y-2.5">
                  <span className="text-gray-500 font-medium">School Name:</span>
                  <span className="text-[#1F2937]">{acceptanceRecord?.school_name || '—'}</span>
                  <span className="text-gray-500 font-medium">Authorized Representative:</span>
                  <span className="text-[#1F2937]">{acceptanceRecord?.signatory_name || '—'}</span>
                  <span className="text-gray-500 font-medium">Designation:</span>
                  <span className="text-[#1F2937]">{acceptanceRecord?.signatory_designation || '—'}</span>
                  <span className="text-gray-500 font-medium">Agreement Version:</span>
                  <span className="text-[#1F2937]">{acceptanceRecord?.version || agreement.version}</span>
                  <span className="text-gray-500 font-medium">Acceptance Date and Time:</span>
                  <span className="text-[#1F2937]">
                    {acceptanceRecord?.accepted_at
                      ? new Date(acceptanceRecord.accepted_at).toLocaleString('en-IN', { dateStyle: 'long', timeStyle: 'medium' })
                      : <span className="text-amber-500 italic">Will be recorded upon submission</span>
                    }
                  </span>
                </div>
              </div>
            </div>
          </div>
        </ScrollArea>
        <div className="px-6 py-4 border-t border-gray-100 bg-white">
          <Button onClick={onClose} className="w-full bg-[#F97D6A] text-white hover:bg-[#F06651] rounded-xl h-10" data-testid="close-agreement-btn">
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
